import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Navbar";
import { api, getUser, canClinical } from "../api";
import { toast } from "sonner";
import { Download, MonitorSmartphone, ShieldCheck, Activity, Wifi, Copy, Check } from "lucide-react";

export default function PamConnect() {
  const { t } = useTranslation();
  const u = getUser();
  const [agents, setAgents] = useState([]);
  const [otc, setOtc] = useState(null);
  const [copied, setCopied] = useState(false);
  const copyOtc = async () => {
    if (!otc?.otc) return;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(otc.otc);
      else throw new Error("no-clipboard");
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch { toast.error(t("pamc.copy_failed")); }
  };
  const [release, setRelease] = useState(null);
  const [dl, setDl] = useState(false);
  const load = () => api.get("/pam-connect/agents").then(({ data }) => setAgents(data.agents || [])).catch(() => {});
  useEffect(() => { load(); api.get("/pam-connect/release/latest").then(({ data }) => setRelease(data)).catch(() => {}); }, []);
  const downloadInstaller = async () => {
    setDl(true);
    try {
      const res = await api.get("/pam-connect/installer", { responseType: "blob" });
      const name = release?.filename || "PAMConnect-Setup-1.0.1-x64.exe";
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error(t("pamc.dl_error")); }
    finally { setDl(false); }
  };
  const enroll = async () => {
    try { const { data } = await api.post("/pam-connect/enroll-start", { display_name: "PAM Connect" }); setOtc(data); load(); }
    catch { toast.error(t("pamc.enroll_err")); }
  };
  const revoke = async (id) => {
    if (!window.confirm(t("pamc.revoke_confirm"))) return;
    try { await api.post(`/pam-connect/agents/${id}/revoke`); toast.success(t("pamc.revoked_ok")); load(); }
    catch { toast.error(t("pamc.revoke_err")); }
  };
  if (u && !canClinical(u)) return <Layout><div className="yp-container py-10 text-slate-600" data-testid="pam-connect-denied">{t("pamc.denied")}</div></Layout>;

  const STATUS_LBL = { online: t("pamc.st_online"), offline: t("pamc.st_offline"), revoked: t("pamc.st_revoked") };
  const STATUS_CLS = { online: "bg-emerald-100 text-emerald-700", offline: "bg-slate-100 text-slate-500", revoked: "bg-red-100 text-red-700" };
  const FEATURES = [
    { Icon: MonitorSmartphone, text: t("pamc.feat1") },
    { Icon: Wifi, text: t("pamc.feat2") },
    { Icon: ShieldCheck, text: t("pamc.feat3") },
    { Icon: Download, text: t("pamc.feat4") },
  ];

  return (<Layout><div className="yp-container py-8 max-w-4xl" data-testid="pam-connect-page">
    {/* Hero — diagnostica cloud, palette blu/teal luminosa */}
    <div className="relative overflow-hidden p-7 sm:p-9 mb-6 rounded-3xl shadow-lg text-white bg-gradient-to-br from-sky-500 via-blue-600 to-teal-500" data-testid="pam-connect-hero">
      <div className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full bg-cyan-300/30 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-28 -left-12 w-72 h-72 rounded-full bg-teal-200/25 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.12]" aria-hidden="true" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "22px 22px" }} />
      <div className="relative">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur px-3 py-1 text-xs font-medium ring-1 ring-white/25">
          <Activity className="w-3.5 h-3.5" /> {t("pamc.badge")}
        </span>
        <h1 className="text-3xl sm:text-4xl font-bold mt-3 tracking-tight">{t("pamc.title")}</h1>
        <p className="text-base sm:text-lg mt-2 text-sky-50/95 max-w-2xl">{t("pamc.tagline")}</p>
        <div className="grid sm:grid-cols-2 gap-3 mt-6 text-sm">
          {FEATURES.map(({ Icon, text }, i) => (
            <div key={i} className="flex gap-3 items-start rounded-xl bg-white/10 backdrop-blur-sm ring-1 ring-white/15 px-3.5 py-3">
              <Icon className="w-5 h-5 shrink-0 text-cyan-100" /><span className="text-sky-50 leading-snug">{text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>

    <div className="yp-card p-6 mb-6 border border-slate-200" data-testid="pam-connect-download-section">
      <h2 className="text-xl font-bold text-slate-900">{t("pamc.dl_title")}</h2>
      <p className="text-sm text-slate-600 mt-1">{t("pamc.dl_desc", { v: release?.version || "1.0.1" })}</p>
      <div className="mt-4 flex items-center gap-3 flex-wrap">
        {release && release.available
          ? <button className="yp-btn yp-btn-primary inline-flex items-center gap-2" onClick={downloadInstaller} disabled={dl} data-testid="pam-connect-download"><Download className="w-4 h-4" />{dl ? t("pamc.dl_progress") : t("pamc.dl_btn")}</button>
          : <button className="yp-btn yp-btn-outline cursor-default" disabled data-testid="pam-connect-download-unavailable">{t("pamc.dl_unavail")}</button>}
        <span className="text-xs text-slate-500">{t("pamc.dl_free")}</span>
      </div>
    </div>

    <div className="flex items-center justify-between mb-3">
      <h2 className="text-xl font-semibold">{t("pamc.my_devices")}</h2>
      <button className="yp-btn yp-btn-primary !py-1.5 !px-4 text-sm" onClick={enroll} data-testid="pam-connect-enroll-btn">{t("pamc.associate")}</button>
    </div>

    {otc && <div className="yp-card p-4 mb-4 border border-blue-200 bg-blue-50" data-testid="pam-connect-otc">
      <div className="text-sm text-slate-600">{t("pamc.otc_hint", { min: Math.round((otc.ttl_seconds || 900) / 60) })}</div>
      <div className="flex items-center gap-2 mt-1">
        <div className="text-2xl font-mono font-bold tracking-wider" data-testid="pam-connect-otc-code">{otc.otc}</div>
        <button type="button" onClick={copyOtc} title={t("pamc.copy_code")} aria-label={t("pamc.copy_code")}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-lg border border-blue-200 bg-white text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors text-sm"
          data-testid="pam-connect-otc-copy">
          {copied ? <><Check size={16} className="text-green-600" /><span className="text-green-700">{t("pamc.copied_code")}</span></> : <Copy size={16} />}
        </button>
      </div>
    </div>}

    {agents.length === 0 && <div className="yp-card p-6 text-slate-400 text-center" data-testid="pam-connect-empty">{t("pamc.empty")}</div>}
    <div className="space-y-3">
      {agents.map((a) => (
        <div key={a.agent_id} className="yp-card p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="pam-connect-agent-row">
          <div>
            <div className="font-semibold">{a.display_name || "PAM Connect"} {a.hostname && <span className="text-slate-400 text-sm">· {a.hostname}</span>}
              <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${STATUS_CLS[a.connectivity] || "bg-slate-100"}`} data-testid="pam-connect-agent-status">{STATUS_LBL[a.connectivity] || a.connectivity}</span></div>
            <div className="text-xs text-slate-500 mt-0.5">{t("pamc.version")} {a.app_version || "—"} · {a.device_count} {t("pamc.devices_unit")} · {t("pamc.last_conn")} {a.last_seen_at ? new Date(a.last_seen_at).toLocaleString() : t("pamc.never")}</div>
          </div>
          {a.status !== "revoked" && <button className="yp-btn yp-btn-ghost !py-1 !px-3 text-xs text-red-600" onClick={() => revoke(a.agent_id)} data-testid="pam-connect-revoke-btn">{t("pamc.revoke")}</button>}
        </div>))}
    </div>
  </div></Layout>);
}
